import { IonRouterLink } from '@ionic/react';
import { useIntl } from 'react-intl';

import styles from '@components/CareProfile.module.css';
import TextWithIcon from '@components/text/TextWithIcon';
import useMyExpert from '@hooks/useMyExpert';
import { FocusAreaEnum } from '@typing/Generated';
import { InitialConsultReportSection } from '@typing/Models';
import { IconName } from '@utils/iconUtils';
import { journeyRoute } from '@utils/routeUtils';

type MenuItemProps = {
  currentSection: InitialConsultReportSection | FocusAreaEnum;
  iconSrc: IconName;
  routerLink: string;
  section: FocusAreaEnum | InitialConsultReportSection;
};

const MenuItem = ({ currentSection, iconSrc, routerLink, section }: MenuItemProps) => {
  const intl = useIntl();

  return (
    <div className={styles.menuItem}>
      <IonRouterLink
        className={
          currentSection === section ||
          (section === InitialConsultReportSection.CLIENT_INFO && currentSection === InitialConsultReportSection.MAIN)
            ? styles.selected
            : styles.notSelected
        }
        data-test={section}
        routerLink={routerLink}
      >
        <TextWithIcon
          icon={iconSrc}
          iconFirst
          text={intl.formatMessage({ id: `pages.client.careProfile.sections.${section}` })}
        />
      </IonRouterLink>
    </div>
  );
};

type Props = {
  consultId?: string;
  currentSection: FocusAreaEnum | InitialConsultReportSection;
  editing?: boolean;
  journeyId: string;
};

const InitialConsultMenu = ({ consultId, currentSection, editing = false, journeyId }: Props) => {
  const myExpert = useMyExpert();

  return (
    <div className={styles.menu}>
      <MenuItem
        currentSection={currentSection}
        iconSrc="user"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}client-info${
            consultId ? `/${consultId}` : ''
          }`,
          myExpert
        })}
        section={InitialConsultReportSection.CLIENT_INFO}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="addressCard"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}basic-info${
            consultId ? `/${consultId}` : ''
          }`,
          myExpert
        })}
        section={InitialConsultReportSection.BASIC_INFO}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="stethoscope"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}medical${
            consultId ? `/${consultId}` : ''
          }`,
          myExpert
        })}
        section={FocusAreaEnum.medical}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="walking"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}functional${
            consultId ? `/${consultId}` : ''
          }`,
          myExpert
        })}
        section={FocusAreaEnum.functional}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="balanceScaleRight"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}legal${consultId ? `/${consultId}` : ''}`,
          myExpert
        })}
        section={FocusAreaEnum.legal}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="usdCircle"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}financial${
            consultId ? `/${consultId}` : ''
          }`,
          myExpert
        })}
        section={FocusAreaEnum.financial}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="handshake"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}social${
            consultId ? `/${consultId}` : ''
          }`,
          myExpert
        })}
        section={FocusAreaEnum.social}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="spa"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}well-being${
            consultId ? `/${consultId}` : ''
          }`,
          myExpert
        })}
        section={FocusAreaEnum.wellbeing}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="spa"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}stage${consultId ? `/${consultId}` : ''}`,
          myExpert
        })}
        section={InitialConsultReportSection.STAGE}
      />
      <MenuItem
        currentSection={currentSection}
        iconSrc="user"
        routerLink={journeyRoute({
          journeyId,
          linkAfterJourney: `/initial-consult-report/${editing ? 'edit/' : ''}conclusion${
            consultId ? `/${consultId}` : ''
          }`,
          myExpert
        })}
        section={InitialConsultReportSection.CONCLUSION}
      />
    </div>
  );
};

export default InitialConsultMenu;
